import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { createAppTheme } from './theme';
import PageLayout from './components/layout/PageLayout';
import AppTemplate from './components/AppTemplate';
import DashboardPage from './features/dashboard/DashboardPage';
import StocksListPage from './features/stocks/StocksListPage';
import StocksLayout from './features/stocks/StocksLayout';
import StocksDashboard from './features/stocks/StocksDashboard';
import ETFsPage from './features/stocks/ETFsPage';
import DividendsPage from './features/stocks/DividendsPage';
import SalesPage from './features/stocks/SalesPage';
import PortfolioAnalysisPage from './features/stocks/PortfolioAnalysisPage';
import InvestmentsImportExportPage from './features/stocks/InvestmentsImportExportPage';
import PerformancePage from './features/stocks/PerformancePage';
import AllocationPage from './features/stocks/AllocationPage';
import MarketTrendsPage from './features/stocks/MarketTrendsPage';
import { AuthProvider, useAuth } from './features/auth/AuthContext';
import StartRedirect from './features/auth/StartRedirect';
import RequireAuth from './features/auth/RequireAuth';
import RequireHousehold from './features/auth/RequireHousehold';
import LoginPage from './features/auth/LoginPage';
import ExpensesPage from './features/expenses/ExpensesPage';
import ExpensesLayout from './features/expenses/ExpensesLayout';
import ProductsPage from './features/expenses/ProductsPage';
import InvoicesPage from './features/expenses/InvoicesPage';
import LocationsPage from './features/expenses/LocationsPage';
import HomeExpenseGroupsPage from './features/expenses/HomeExpenseGroupsPage';
import HomeExpensesPage from './features/expenses/HomeExpensesPage';
import PersonalExpensesPage from './features/expenses/PersonalExpensesPage';
import HomeExpenseImportExport from './components/HomeExpenseImportExport';
import AccountsPage from './features/expenses/AccountsPage';
import CategoriesPage from './features/expenses/CategoriesPage';
import BudgetsPage from './features/expenses/pages/BudgetsPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import MobileAppPage from './features/mobileApp/MobileAppPage';
import PlannerLayout from './features/planner/PlannerLayout';
import MorePage from './features/planner/MorePage';
import AccountPage from './features/planner/account/AccountPage';
import HouseholdRoutes from './features/planner/household';
import CatalogRoutes from './features/planner/catalog';
import RecipesRoutes from './features/planner/recipes';
import MealsRoutes from './features/planner/meals';
import FreezerRoutes from './features/planner/freezer';
import ShoppingRoutes from './features/planner/shopping';
import TripsRoutes from './features/planner/trips';
import TasksRoutes from './features/planner/tasks';
import CalendarRoutes from './features/planner/calendar';
import BoardPage from './features/planner/calendar/BoardPage';
import PantryRoutes from './features/planner/pantry';
import WeekRoutes from './features/planner/week';
import AssistantRoutes from './features/planner/assistant';

/** Light, dark or as the browser, as chosen in "A minha conta". */
const AppTheme: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useAuth();
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const choice = settings?.theme ?? 'System';
  const mode = choice === 'Dark' || (choice === 'System' && prefersDark) ? 'dark' : 'light';
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <AppTheme>
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/inicio" element={<RequireAuth><StartRedirect /></RequireAuth>} />
            {/* The household board fills the screen of a tablet or TV, without the app's bars */}
            <Route path="/casa/painel" element={<RequireAuth><BoardPage /></RequireAuth>} />
            <Route path="/" element={<AppTemplate />}>
              {/* Pages without a section sidebar */}
              <Route element={<PageLayout />}>
                <Route index element={
                  <RequireAuth>
                    <RequireHousehold>
                      <DashboardPage />
                    </RequireHousehold>
                  </RequireAuth>
                } />
                <Route path="/user/edit" element={<Navigate to="/casa/conta" replace />} />
                {/* Public, so the app can be installed before signing in */}
                <Route path="/mobile-app" element={<MobileAppPage />} />
              </Route>
              <Route path="stocks" element={
                <RequireAuth>
                  <RequireHousehold>
                    <StocksLayout />
                  </RequireHousehold>
                </RequireAuth>
              }>
                <Route index element={<StocksDashboard />} />
                <Route path="list" element={<StocksListPage />} />
                <Route path="etfs" element={<ETFsPage />} />
                <Route path="dividends" element={<DividendsPage />} />
                <Route path="sales" element={<SalesPage />} />
                <Route path="portfolio" element={<PortfolioAnalysisPage />} />
                <Route path="performance" element={<PerformancePage />} />
                <Route path="allocation" element={<AllocationPage />} />
                <Route path="trends" element={<MarketTrendsPage />} />
                <Route path="import-export" element={<InvestmentsImportExportPage />} />
              </Route>
              <Route path="expenses" element={
                <RequireAuth>
                  <RequireHousehold>
                    <ExpensesLayout />
                  </RequireHousehold>
                </RequireAuth>
              }>
                <Route index element={<ExpensesPage />} />
                <Route path="home-expenses" element={<HomeExpensesPage />} />
                <Route path="import-export" element={<HomeExpenseImportExport />} />
                <Route path="groups" element={<HomeExpenseGroupsPage />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="accounts" element={<AccountsPage />} />
                <Route path="personal-expenses" element={<PersonalExpensesPage />} />
                <Route path="personal-groups" element={<HomeExpenseGroupsPage />} />
                <Route path="personal-categories" element={<CategoriesPage />} />
                <Route path="personal-accounts" element={<AccountsPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="locations" element={<LocationsPage />} />
                <Route path="budgets" element={<BudgetsPage />} />
              </Route>
              {/* Household planner: the same screens as the Android app */}
              <Route path="casa" element={<RequireAuth><PlannerLayout /></RequireAuth>}>
                <Route index element={<Navigate to="semana" replace />} />
                <Route path="semana/*" element={<WeekRoutes />} />
                <Route path="tarefas/*" element={<TasksRoutes />} />
                <Route path="eventos/*" element={<CalendarRoutes />} />
                <Route path="refeicoes/*" element={<MealsRoutes />} />
                <Route path="receitas/*" element={<RecipesRoutes />} />
                <Route path="congelador/*" element={<FreezerRoutes />} />
                <Route path="dizer/*" element={<AssistantRoutes />} />
                <Route path="compras/*" element={<ShoppingRoutes />} />
                <Route path="em-casa/*" element={<PantryRoutes />} />
                <Route path="idas/*" element={<TripsRoutes />} />
                <Route path="mais" element={<MorePage />} />
                <Route path="conta" element={<AccountPage />} />
                <Route path="agregado/*" element={<HouseholdRoutes />} />
                <Route path="catalogo/*" element={<CatalogRoutes />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </AppTheme>
  </AuthProvider>
);

export default App;
